import { useEffect, useRef, useState } from "react";
import { Room, RoomEvent, Track, VideoPresets } from "livekit-client";
import { api } from "@/lib/api";

/** Shared LiveKit room hook — host publishes, viewers subscribe. Chat rides on data channels + is persisted. */
export function useLiveRoom({ postId, role, enabled }) {
  const roomRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | connecting | live | ended | error
  const [error, setError] = useState(null);
  const [viewers, setViewers] = useState(0);
  const [messages, setMessages] = useState([]);
  const [videoEl, setVideoEl] = useState(null);
  const [localStream, setLocalStream] = useState(null);

  useEffect(() => {
    if (!enabled || !postId) return undefined;
    let cancelled = false;
    const room = new Room({
      adaptiveStream: true, dynacast: true,
      videoCaptureDefaults: { resolution: VideoPresets.h1080.resolution },
      publishDefaults: { simulcast: true, videoSimulcastLayers: [VideoPresets.h360, VideoPresets.h720], videoEncoding: VideoPresets.h1080.encoding },
    });
    roomRef.current = room;
    const count = () => setViewers(role === "host" ? room.remoteParticipants.size : Math.max(0, room.remoteParticipants.size));
    const attach = (track) => {
      if (track.kind === Track.Kind.Video) setVideoEl(track.attach());
      else if (track.kind === Track.Kind.Audio) track.attach();
    };
    room.on(RoomEvent.TrackSubscribed, attach);
    room.on(RoomEvent.TrackUnsubscribed, (t) => { t.detach().forEach((el) => el.remove()); if (t.kind === Track.Kind.Video) setVideoEl(null); });
    room.on(RoomEvent.ParticipantConnected, count);
    room.on(RoomEvent.ParticipantDisconnected, count);
    room.on(RoomEvent.Disconnected, () => !cancelled && setState((s) => (s === "live" ? "ended" : s)));
    room.on(RoomEvent.DataReceived, (payload, participant, _k, topic) => {
      if (topic !== "chat") return;
      try { const m = JSON.parse(new TextDecoder().decode(payload)); setMessages((l) => [...l.slice(-199), { ...m, from: participant?.name || "Guest" }]); } catch { /* ignore */ }
    });
    (async () => {
      setState("connecting");
      try {
        const { data } = await api.post(`/live/${postId}/${role === "host" ? "host-token" : "watch-token"}`);
        const hist = await api.get(`/live/${postId}/chat`).catch(() => ({ data: { messages: [] } }));
        if (cancelled) return;
        setMessages(hist.data.messages.map((m) => ({ text: m.text, from: m.name, at: m.at })));
        await room.connect(data.ws_url, data.token, { autoSubscribe: true });
        room.remoteParticipants.forEach((p) => p.trackPublications.forEach((pub) => pub.track && attach(pub.track)));
        if (role === "host") {
          await room.localParticipant.setCameraEnabled(true, { facingMode: "user", resolution: VideoPresets.h1080.resolution });
          await room.localParticipant.setMicrophoneEnabled(true);
          const ms = new MediaStream();
          room.localParticipant.trackPublications.forEach((pub) => pub.track?.mediaStreamTrack && ms.addTrack(pub.track.mediaStreamTrack));
          setLocalStream(ms);
          const v = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track;
          if (v) setVideoEl(v.attach());
        }
        count();
        setState("live");
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.detail || e.message || "Could not connect");
        setState("error");
      }
    })();
    return () => { cancelled = true; room.disconnect(); roomRef.current = null; };
  }, [postId, role, enabled]);

  const send = async (text) => {
    const room = roomRef.current;
    if (!room || !text.trim()) return;
    const m = { text: text.trim(), at: Date.now() };
    await room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(m)), { reliable: true, topic: "chat" });
    setMessages((l) => [...l.slice(-199), { ...m, from: "You", me: true }]);
    api.post(`/live/${postId}/chat`, { text: m.text }).catch(() => {});
  };
  const flipCamera = async () => {
    const room = roomRef.current;
    const pub = room?.localParticipant.getTrackPublication(Track.Source.Camera);
    if (!pub?.track) return;
    const cur = pub.track.mediaStreamTrack.getSettings().facingMode;
    await pub.track.restartTrack({ facingMode: cur === "user" ? "environment" : "user" });
  };
  const toggleMic = async () => { const r = roomRef.current; if (r) await r.localParticipant.setMicrophoneEnabled(!r.localParticipant.isMicrophoneEnabled); };
  const disconnect = () => roomRef.current?.disconnect();

  return { state, error, viewers, messages, videoEl, localStream, send, flipCamera, toggleMic, disconnect };
}
